import { Injectable } from '@angular/core';
import {
  convertCommunicationIdToElementDesignId,
  convertEquipmentNumberToVisitationEquipmentNumber,
  filterUndefined,
  IUserProfile,
} from 'src/app/lego-utils';
import { Client, ResponseType } from '@microsoft/microsoft-graph-client';
import { isValid, parse } from 'date-fns';
import { first } from 'lodash-es';
import type { ProfilePhoto, User } from 'microsoft-graph';
import { combineLatest, defer, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, switchMap } from 'rxjs/operators';
import { IUser } from 'src/app/models';
import {
  ElementHistoryDocument,
  ElementHistoryLog,
  ElementRiskAssessment,
  ProcessingHistoryDocument,
  TechnicalReview,
  Visitation,
} from 'src/app/models/domain';
import { MouldDecisionToolAdapter, VisitationAdapter } from 'src/app/models/domain/adapters';
import {
  IGraphVisitationItem,
  ISharePointEraItem,
  ISharePointProcessingHistoryItem,
  ISharePointSiriusItem,
  ISharePointTechnicalReviewItem,
  ISharePointUsedRangeResponse,
} from 'src/app/models/domain/sharepoint-interfaces';
import { environment } from 'src/environments/environment';
import { MsalAuthProvider } from './msal-auth.provider';

/**
 * A photo size.
 * The supported sizes of HD photos on Office 365 are as follows: 48x48, 64x64, 96x96, 120x120, 240x240, 360x360, 432x432, 504x504, and 648x648.
 * @remarks
 * Photos can be any dimension if they are stored in Azure Active Directory.
 */
type ImageSize = string;

interface IUserParams {
  /**
   * The user id can be either:
   * - id (GUID)
   * - userPrincipalName (email)
   *
   * If `undefined` the currently signed in user will be fetched.
   */
  userId?: string;
  /**
   * The desired image size.
   * Beware that all sizes does not exist.
   *
   * If `undefined` the largest available photo will be used.
   */
  size?: ImageSize;
}

interface ISharePointResponse<T> {
  value: T[];
}

@Injectable({ providedIn: 'root' })
export class SharePointService {
  private readonly graphClient: Client;

  /** Used in the $select parameter for user profile lookups */
  private readonly selectedUserFields = [
    'id',
    'mailNickname', // this contains the short name
    'mail',
    'displayName',
    'givenName',
    'surname',
    'jobTitle',
    'department',
  ];

  public constructor(
    private readonly visitationAdapter: VisitationAdapter,
    private readonly mdtAdapter: MouldDecisionToolAdapter,
    msalAuthProvider: MsalAuthProvider
  ) {
    this.graphClient = Client.init({
      authProvider: async (done) => {
        try {
          const token = await msalAuthProvider.getAccessToken({
            scopes: ['profile', 'User.Read', 'User.ReadBasic.All'],
          });

          done(undefined, token);
        } catch (error: unknown) {
          done(
            error,
            // eslint-disable-next-line no-null/no-null
            null
          );
        }
      },
    });
  }

  public getUser(params: IUserParams = {}): Observable<IUser> {
    return combineLatest([
      this.getUserProfile(params.userId),
      this.getUserPhoto(params).pipe(
        // in case of any error with the image just give an empty src
        catchError(() => of(undefined))
      ),
    ]).pipe(map(([user, image]) => ({ ...user, imageSrc: image })));
  }

  public getUserProfile(userId?: string): Observable<Omit<IUser, 'imageSrc'>> {
    const apiEndpoint = userId ? `/users/${userId}` : '/me';

    const request = this.graphClient
      .api(apiEndpoint)
      .select(this.selectedUserFields)
      .responseType(ResponseType.JSON);

    return defer(() => request.get()).pipe(map((user: User) => this.mapUser(user)));
  }

  public searchUserProfiles(search: string): Observable<Array<Omit<IUser, 'imageSrc'>>> {
    const request = this.graphClient
      .api('/users')
      .select(this.selectedUserFields)
      .filter(this.buildUserSearchFilter(search))
      .responseType(ResponseType.JSON);

    return defer(() => request.get()).pipe(
      map(({ value }: { value: User[] }) => value.map((user) => this.mapUser(user)))
    );
  }

  /**
   * {@link searchUserProfiles} plus a lazy per-user `imageUrl$`. A missing photo completes
   * without emitting, so the avatar falls back to its icon rather than erroring the stream.
   */
  public searchUserProfilesWithPhotos(search: string, limit?: number): Observable<IUserProfile[]> {
    let request = this.graphClient
      .api('/users')
      .select(this.selectedUserFields)
      .filter(this.buildUserSearchFilter(search))
      .responseType(ResponseType.JSON);

    if (limit) {
      request = request.top(limit);
    }

    return defer(() => request.get()).pipe(
      map(({ value }: { value: User[] }) =>
        value.map((user) => ({
          ...this.mapUser(user),
          imageUrl$: this.getUserPhoto({ userId: user.id ?? undefined }).pipe(
            catchError(() => EMPTY)
          ),
        }))
      )
    );
  }

  public getUserPhotoSizes(userId?: string): Observable<ProfilePhoto[]> {
    const apiEndpoint = userId ? `/users/${userId}` : '/me';
    const request = this.graphClient.api(`${apiEndpoint}/photos`).responseType(ResponseType.JSON);

    return defer(() => request.get()).pipe(map(({ value }: { value: ProfilePhoto[] }) => value));
  }

  public getUserPhoto(params: IUserParams = {}): Observable<string> {
    let apiEndpoint = params.userId ? `/users/${params.userId}` : '/me';
    // /photo gets the largest available photo
    apiEndpoint += params.size ? `/photos/${params.size}` : '/photo';
    const request = this.graphClient.api(`${apiEndpoint}/$value`).responseType(ResponseType.BLOB);

    return defer(() => request.get()).pipe(
      switchMap((blob: Blob) => {
        const subject = new Subject<string>();
        // URL.createObjectURL doesn't work as it'll generate
        // data:blob:http://localhost...
        // it should be: data:image/jpeg,...
        const reader = new FileReader();

        reader.onerror = () => {
          subject.error(reader.error);
        };

        reader.onloadend = () => {
          subject.next(reader.result as string);
        };

        reader.readAsDataURL(blob);

        return subject;
      })
    );
  }

  public getElementRiskAssessmentsForCommunicationId(
    communicationId: string
  ): Observable<ElementRiskAssessment[]> {
    const eraDriveId = environment.sharePointERADriveId;

    const request = this.getSharePointItemsByIndexedField(
      eraDriveId,
      'Design_x0020_No',
      [communicationId, convertCommunicationIdToElementDesignId(communicationId)],
      ['Conclusion', 'Material_x0020_No']
    )
      .expand('driveItem($select=name,webUrl)')
      .select('id');

    return defer(() => request.get() as Promise<ISharePointResponse<ISharePointEraItem>>).pipe(
      map((response) =>
        response.value.map(
          (era) =>
            new ElementRiskAssessment(
              era.id,
              era.driveItem.name,
              new URL(era.driveItem.webUrl),
              era.fields.Conclusion,
              era.fields.Material_x0020_No
            )
        )
      )
    );
  }

  public getTechnicalReviewDocumentsForCommunicationId(
    communicationId: string
  ): Observable<TechnicalReview[]> {
    const trDriveId = environment.sharePointTRDriveId;

    const request = this.getSharePointItemFromName(trDriveId, `${communicationId}_TR`).select([
      'id',
      'name',
      'webUrl',
    ]);

    return defer(
      () => request.get() as Promise<ISharePointResponse<ISharePointTechnicalReviewItem>>
    ).pipe(
      map((response) =>
        response.value.map((tr) => new TechnicalReview(tr.id, tr.name, new URL(tr.webUrl)))
      )
    );
  }

  public getProcessingHistoryDocumentsForCommunicationId(
    communicationId: string
  ): Observable<ProcessingHistoryDocument[]> {
    const phdDriveId = environment.sharePointPHDDriveId;

    const request = this.getSharePointItemsByIndexedField(
      phdDriveId,
      'Design_ID',
      communicationId,
      ['Design_name', 'Project', 'Visibility_x0020_status']
    )
      .expand('driveItem($select=name,webUrl)')
      .select('id');

    return defer(
      () => request.get() as Promise<ISharePointResponse<ISharePointProcessingHistoryItem>>
    ).pipe(
      map(({ value }) =>
        value.map(
          (phd) =>
            new ProcessingHistoryDocument(
              phd.id,
              phd.driveItem.name,
              phd.fields.Design_ID,
              phd.fields.Design_name,
              phd.fields.Project,
              phd.fields.Visibility_x0020_status,
              new URL(phd.driveItem.webUrl)
            )
        )
      )
    );
  }

  public getElementHistoryDocumentsForDesignId(
    designId: string
  ): Observable<ElementHistoryDocument[]> {
    const ehdDriveId = environment.sharePointEHDDriveId;

    const request = this.getSharePointItemFromName(ehdDriveId, designId).select([
      'id',
      'name',
      'webUrl',
    ]);

    return defer(
      () => request.get() as Promise<ISharePointResponse<ISharePointTechnicalReviewItem>>
    ).pipe(
      map(({ value }) =>
        value.map((ehd) => new ElementHistoryDocument(ehd.id, ehd.name, new URL(ehd.webUrl)))
      )
    );
  }

  public getSiriusElementHistoryDocumentsForDesignId(
    designId: string
  ): Observable<ElementHistoryDocument[]> {
    const ehdSiriusDriveId = environment.sharePointSiriusEHDDriveId;

    const request = this.getSharePointItemFromName(ehdSiriusDriveId, designId).select([
      'id',
      'name',
      'webUrl',
      'createdDateTime',
    ]);

    return defer(() => request.get() as Promise<ISharePointResponse<ISharePointSiriusItem>>).pipe(
      map(({ value }) =>
        value.map(
          (item) =>
            new ElementHistoryDocument(
              item.id,
              item.name,
              new URL(item.webUrl),
              new Date(item.createdDateTime)
            )
        )
      )
    );
  }

  public getElementHistoryLogForDesignId(designId: string): Observable<{
    webUrl?: URL;
    log: ElementHistoryLog[];
    worksheetName?: string;
  }> {
    const ehdDriveId = environment.sharePointEHDDriveId;

    return this.getElementHistoryDocumentsForDesignId(designId).pipe(
      map(first),
      filter((value): value is ElementHistoryDocument => value !== undefined),
      switchMap(({ id, webUrl }) =>
        this.findWorksheetIdFromName(ehdDriveId, id, 'Element Log').pipe(
          switchMap(({ worksheetId, worksheetName }) =>
            this.getElementHistoryLogFromWorksheetId(id, worksheetId).pipe(
              map((log) => ({
                webUrl,
                log,
                worksheetName,
              }))
            )
          )
        )
      ),
      defaultIfEmpty({
        log: [],
      })
    );
  }

  public getVisitationDocumentByEquipmentNumber(equipmentNumber: string): Observable<Visitation[]> {
    const visitationDriveId = environment.sharePointVisitationDriveId;

    const selectProps = [
      'id',
      'Title',
      'Order_x0020_Type',
      'Part_x0020_number',
      'C64_x0020_Date',
      'Material_x0020_1',
      'Material_x0020_2',
      'Planning_x0020_Status',
      'Dev_x0020_Segment',
      'Mould_x0020_definition',
      'Mould_x0020_type',
      'Cavities',
      'MM_x0020_Profile',
      'MQ_x0020_Profile',
      'Mould1_x0020_plant',
      'TED_x0020_Start',
      'TED_x0020_End_x0020__x0028_C45_x0029_',
      'PTY_x0020_Start',
      'PTY_x0020_End_x0020__x0028_C46_x0029_',
      'PTY_x0020_Eval_x0020_start',
      'PTY_x0020_Eval_x0020_End_x0020__x0028_C47_x0029_',
    ];

    const request = this.graphClient
      .api(`/drives/${visitationDriveId}/root/children`)
      .filter(
        `startswith(name, '${convertEquipmentNumberToVisitationEquipmentNumber(equipmentNumber)}')`
      )
      .expand(`listItem($expand=fields($select=${selectProps.join(',')}))`);

    return defer(() => request.get()).pipe(
      map((response: { value: IGraphVisitationItem[] }) =>
        response.value.map((item) =>
          this.visitationAdapter.adaptToDomain({
            fileName: item.name,
            webUrl: item.webUrl,
            ...item.listItem.fields,
            Title: '',
            Mould_x0020_type: '',
          })
        )
      )
    );
  }

  public getMDTDocumentByEquipmentNumber(equipmentNumber: string) {
    const mdtDriveId = environment.sharePointMDTDriveId;

    const request = this.graphClient
      .api(`/drives/${mdtDriveId}/root/children`)
      .filter(`startswith(name, '${equipmentNumber}')`)
      .expand(`listItem($expand=fields)`);

    return defer(() => request.get()).pipe(
      map(({ value }: { value: IGraphVisitationItem[] }) => {
        return value.map((response) =>
          this.mdtAdapter.adaptToDomain({
            fileName: response.name,
            webUrl: response.webUrl,
            ...response.listItem.fields,
          })
        );
      })
    );
  }

  private buildUserSearchFilter(search: string): string {
    // OData escapes a single quote by doubling it; without this a name like O'Brien
    // terminates the literal and produces a malformed $filter.
    const escaped = search.replace(/'/gu, "''");

    return ['mailNickname', 'mail', 'displayName', 'givenName', 'surname', 'jobTitle']
      .map((field) => `startswith(${field}, '${escaped}')`)
      .join(' or ');
  }

  private mapUser(user: User): Omit<IUser, 'imageSrc'> {
    return {
      displayName: user.displayName ?? undefined,
      email: user.mail ?? undefined,
      firstName: user.givenName ?? undefined,
      id: user.id ?? undefined,
      jobTitle: user.jobTitle ?? undefined,
      lastName: user.surname ?? undefined,
      shortName: user.mailNickname ?? undefined,
      department: user.department ?? undefined,
    };
  }

  private getSharePointItemFromName(driveId: string, ...names: string[]) {
    const filterExpression = names.map((name) => `startswith(name, '${name}')`).join(' or ');

    return this.graphClient
      .api(`/drives/${driveId}/root/children`)
      .filter(filterExpression)
      .responseType(ResponseType.JSON);
  }

  private getSharePointItemsByIndexedField(
    driveId: string,
    indexedFieldName: string,
    searchString: string[] | string,
    selectFields?: string[]
  ) {
    const searchConditions = searchString instanceof Array ? searchString : [searchString];

    const filterExpression = searchConditions
      .map((value) => `fields/${indexedFieldName} eq '${value}'`)
      .join(' or ');

    let expandSelect = '';

    if (selectFields && selectFields.length > 0) {
      if (!selectFields.includes(indexedFieldName)) {
        // we can't filter on something that isn't $selected
        // so if the callee didn't add indexedFieldName to $select
        // then we do it for them
        // remember that if there is no $select then everything is selected
        selectFields.push(indexedFieldName);
      }

      expandSelect = `($select=${selectFields.join(',')})`;
    }

    return this.graphClient
      .api(`/drives/${driveId}/list/items`)
      .filter(filterExpression)
      .expand(`fields${expandSelect}`)
      .responseType(ResponseType.JSON);
  }

  private findWorksheetIdFromName(
    driveId: string,
    sharePointItemId: string,
    name: string
  ): Observable<{ worksheetId: string; worksheetName: string }> {
    const response = this.graphClient
      .api(`/drives/${driveId}/items/${sharePointItemId}/workbook/worksheets`)
      .responseType(ResponseType.JSON);

    return defer(() => response.get()).pipe(
      map((res: { value: Array<{ id?: string; name?: string }> }) =>
        res.value
          .map(({ id, name: worksheetName }) => {
            if (
              typeof id !== 'string' ||
              id.length === 0 ||
              typeof worksheetName !== 'string' ||
              worksheetName.length === 0
            ) {
              throw new Error('Invalid worksheet metadata from SharePoint');
            }

            return { worksheetId: id, worksheetName };
          })
          .find(({ worksheetName }) => worksheetName.includes(name))
      ),
      filterUndefined()
    );
  }

  private getElementHistoryLogFromWorksheetId(
    sharePointItemId: string,
    worksheetId: string
  ): Observable<ElementHistoryLog[]> {
    const ehdDriveId = environment.sharePointEHDDriveId;

    const response = this.graphClient
      .api(
        `/drives/${ehdDriveId}/items/${sharePointItemId}/workbook/worksheets/${encodeURI(
          worksheetId
        )}/usedRange`
      )
      .responseType(ResponseType.JSON);

    return defer(() => response.get()).pipe(
      map((res: ISharePointUsedRangeResponse): ElementHistoryLog[] =>
        this.elementHistoryLogFromWorksheet(res)
      )
    );
  }

  /**
   * Process the EHD excel worksheet.
   * @param worksheet - is the worksheet for element log in the EHD excel document, it is expected to contain 3 lines of nothing, and from there the log should start.
   */
  private elementHistoryLogFromWorksheet({
    values,
  }: ISharePointUsedRangeResponse): ElementHistoryLog[] {
    return values
      .slice(3)
      .filter((row) => row[1] || row[2] || row[3])
      .map((row) => new ElementHistoryLog(row[2], row[3], this.parseElementLogDate(row[1])));
  }

  private parseElementLogDate(dateString: number | string): Date | undefined {
    if (typeof dateString === 'number') {
      return this.fromExcelSerialDate(dateString);
    }

    const formats = ['dd.MM.y', 'M/d/yyyy', 'MM/dd/y', 'dd-MM-yyyy'];

    for (const fmt of formats) {
      const parsed = parse(dateString, fmt, new Date());

      if (isValid(parsed)) {
        return parsed;
      }
    }

    return undefined;
  }

  private fromExcelSerialDate(serial: number): Date {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Excel epoch starts on 30 Dec 1899

    return new Date(excelEpoch.getTime() + serial * 86400000); // 86400000 ms in a day
  }
}
